/** @module A block of log lines: level in colour, the rest of the record on demand. */
import { useTranslation } from "react-i18next";

import type { components } from "@/api/client";

type Line = components["schemas"]["LogLineOut"];

const TONE: Record<string, string> = {
  debug: "var(--text-faint)",
  info: "var(--text-muted)",
  warning: "var(--warn)",
  error: "var(--loss)",
  critical: "var(--loss)",
};

function time(value: string | null, locale: string): string {
  if (!value) return "";
  const moment = new Date(value);
  return Number.isNaN(moment.getTime()) ? value : moment.toLocaleTimeString(locale);
}

export function LogView({ lines, empty }: { lines: Line[]; empty: string }) {
  const { i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "de";

  if (lines.length === 0) return <p className="t-muted">{empty}</p>;

  return (
    // Newest at the bottom, as in a terminal; the box scrolls, the page does not.
    <div className="max-h-[28rem] overflow-auto rounded-[var(--radius-sm)] bg-(--surface) p-3 font-mono text-[length:var(--text-xs)]">
      {lines.map((line, index) => {
        const extra = Object.entries(line.fields);
        return (
          <div key={`${line.timestamp ?? ""}-${index}`} className="flex gap-2 py-px">
            <span className="shrink-0 text-(--text-faint)">{time(line.timestamp, locale)}</span>
            <span
              className="w-16 shrink-0 uppercase"
              style={{ color: TONE[line.level ?? ""] ?? "var(--text-muted)" }}
            >
              {line.level ?? ""}
            </span>
            <span className="min-w-0 break-words">
              {line.event ?? line.text}
              {extra.length > 0 ? (
                <span className="t-muted">
                  {extra.map(([key, value]) => ` ${key}=${String(value)}`).join("")}
                </span>
              ) : null}
            </span>
          </div>
        );
      })}
    </div>
  );
}
